/**
 * @author       hugo@zumper.com (Hugo Pineda)
 * @copyright    Copyright (c) 2020, Zumper
 */

import { selectEntity, selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { LeaseType, getEnumLabel } from '@zcode/enums'
import { isErrorish } from '@zcode/error-filters'
import { DETAIL_PAGE_TYPES, EVENT_NAMES, PAGE_NAMES, PROP_NAMES } from '@zcode/mixpanel'
import { toTitleCase } from '@zcode/nr/es/shared/utils'
import type { Property, State } from '@zcode/state-type'
import { getCurrentCityPath } from '../../../inspectors'
import { logger } from '../../../logger'
import { deleteUndefineds } from './deleteUndefineds'
import { getDetailProps } from './getDetailProps'
import { getFilterProps } from './getFilterProps'
import { getFormattedOrigin } from './getFormattedOrigin'
import { getPageNameFromRouteProps } from './getPageNameFromRouteProps'
import { mapFeatures } from './mapFeatures'

export const getCommonProps = ({
  state,
  entity,
  pageName,
  origin,
  event,
}: {
  state?: State
  entity?: Property | null
  pageName?: string
  origin?: string
  event?: string
} = {}) => {
  if (!state) {
    return {}
  }
  let pageType
  let buildingId
  let cityState
  let features
  let feedName
  let leaseType
  let listingId
  let maxLeaseDays
  let minLeaseDays
  let promotionType
  let specials

  const formattedOrigin = origin && getFormattedOrigin(origin)

  // entity can be passed in from message modals, if not we look in redux
  if (!entity) {
    entity = selectEntity(state)
  }

  // if no pageName is passed in, we can figure it out from current location
  if (!pageName) {
    const isMobile = selectIsMobile(state)

    pageName = getPageNameFromRouteProps({
      routeProps: {
        location: { pathname: window?.location?.pathname },
      },
      isMobile,
    })
  }

  try {
    if (entity) {
      cityState = entity.cityState
      leaseType = entity.leaseType
        ? toTitleCase(getEnumLabel(LeaseType, entity.leaseType))
        : undefined
      maxLeaseDays = entity.maxLeaseDays ?? undefined
      minLeaseDays = entity.minLeaseDays ?? undefined
      feedName = entity.feedName
      specials = entity.specials ?? undefined
      promotionType = entity.promotionType
      if (pageName !== PAGE_NAMES.SEARCH && pageName !== PAGE_NAMES.SEARCH_LIST) {
        if (entity.isBuilding) {
          pageType = DETAIL_PAGE_TYPES.BUILDING
        } else {
          pageType = DETAIL_PAGE_TYPES.LISTING
        }
      }
      if (entity.features) {
        features = mapFeatures(entity.features)
      }
      if (entity.buildingId) {
        buildingId = entity.buildingId
      } else {
        listingId = entity.listingId
      }
    } else {
      cityState = getCurrentCityPath(state)
    }
  } catch (e: unknown) {
    if (isErrorish(e)) {
      logger.error(`Error gathering props for mixpanel: ${e.message}`)
    }
  }

  const detailPageProps =
    entity &&
    (event === EVENT_NAMES.VIEW_PAGE || event === EVENT_NAMES.DETAIL_PAGE_TOP_SIMILAR_LISTINGS) &&
    pageName === PAGE_NAMES.DETAIL
      ? getDetailProps(state, entity)
      : {}

  const allProps = {
    // common props
    [PROP_NAMES.BUILDING_ID]: buildingId,
    [PROP_NAMES.CITY_STATE_LOCATION]: cityState,
    [PROP_NAMES.DETAIL_PAGE_TYPE]: pageType,
    [PROP_NAMES.FEED_NAME]: feedName,
    [PROP_NAMES.FORMATTED_ORIGIN]: formattedOrigin,
    [PROP_NAMES.LEASE_TYPE]: leaseType,
    [PROP_NAMES.LISTING_ID]: listingId,
    [PROP_NAMES.MAX_LEASE_DAYS]: maxLeaseDays,
    [PROP_NAMES.MIN_LEASE_DAYS]: minLeaseDays,
    [PROP_NAMES.ORIGIN]: origin,
    [PROP_NAMES.PAGE_NAME]: pageName,
    [PROP_NAMES.PROMO_TYPE]: promotionType,
    [PROP_NAMES.PROPERTY_FEATURE]: features,
    [PROP_NAMES.SPECIALS]: specials,

    ...getFilterProps(state),

    ...detailPageProps,
  }

  deleteUndefineds(allProps)
  return allProps
}
